import React from 'react';
import DownloadAppPrompt from './DownloadAppPrompt';
import { OrderItem } from '../types';

export type { OrderItem };

interface SubOrder {
    _id: string;
    plantName: string;
    quantity: number;
}

interface TrackOrdersSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    ordersLoading: boolean;
    userOrders: OrderItem[];
    session?: any; 
}

const TrackOrdersSidebar: React.FC<TrackOrdersSidebarProps> = ({ isOpen, onClose, ordersLoading, userOrders, session }) => {
    if (!isOpen) return null;

    const groupedOrders: any[] = [];
    const sortedOrders = [...userOrders].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    sortedOrders.forEach(order => {
        const orderTime = new Date(order.createdAt).getTime();
        const matchingGroup = groupedOrders.find(g => Math.abs(new Date(g.createdAt).getTime() - orderTime) <= 5000);
        if (matchingGroup) {
            matchingGroup.subOrders.push(order);
            matchingGroup.totalAmount += Number(order.totalAmount) || 0;
        } else {
            const isCompleted = order.status === 'completed' || order.status === 'pickup_confirmed';
            groupedOrders.push({ _id: order._id, createdAt: order.createdAt, status: isCompleted ? 'completed' : 'active', totalAmount: Number(order.totalAmount) || 0, subOrders: [order] });
        }
    });

    const activeGroups = groupedOrders.filter(g => g.status === 'active');
    const completedGroups = groupedOrders.filter(g => g.status === 'completed');

    const renderOrderCard = (group: any) => (
        <div key={group._id} className="p-4 bg-white rounded-2xl border border-gray-150 space-y-3 shadow-sm mb-1.5 text-left">
            <div className="flex justify-between items-start">
                <div>
                    <h4 className="font-extrabold text-gray-900 text-sm">Order #{group._id.substring(0, 8).toUpperCase()}</h4>
                    <p className="text-xs text-gray-400 font-medium mt-0.5">{new Date(group.createdAt).toLocaleDateString()}</p>
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md uppercase ${group.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{group.status}</span>
            </div>
            <div className="border-t border-b divide-y py-1">
                {group.subOrders.map((so: SubOrder) => (
                    <div key={so._id} className="flex justify-between text-xs py-1">
                        <span>{so.plantName}</span>
                        <strong>x{so.quantity}</strong>
                    </div>
                ))}
            </div>
            <div className="flex justify-between text-xs font-bold text-green-600">
                <span>Total:</span>
                <span>${group.totalAmount.toFixed(2)}</span>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-[150] flex justify-end text-left">
            <div className="absolute inset-0" onClick={onClose}></div>
            <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between z-10 animate-in slide-in-from-right duration-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 relative flex-shrink-0 text-left">
                    <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-white/60">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <div className="mt-2">
                        <h3 className="text-2xl font-black text-gray-900 tracking-tight" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Track Your Orders 📦</h3>
                    </div>
                </div>

                <div className="p-6 flex-grow flex flex-col min-h-0">
                    {ordersLoading ? (
                        <div className="text-center py-10 font-medium text-gray-500 flex-grow">Loading tracking sheets...</div>
                    ) : userOrders.length === 0 ? (
                        <div className="flex flex-col h-full min-h-0 space-y-4">
                            <div className="flex-1 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/70 flex flex-col">
                                <h4 className="text-sm font-black text-blue-900 tracking-tight px-1 pb-2 flex-shrink-0" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Active Orders</h4>
                                <div className="flex-grow flex items-center justify-center text-center text-blue-400 text-xs font-medium bg-white/60 rounded-xl border border-dashed border-blue-200 italic">No active orders</div>
                            </div>
                            <div className="flex-1 p-4 bg-green-50/50 rounded-2xl border border-green-100/70 flex flex-col">
                                <h4 className="text-sm font-black text-green-900 tracking-tight px-1 pb-2 flex-shrink-0" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Completed Orders</h4>
                                <div className="flex-grow flex items-center justify-center text-center text-green-400 text-xs font-medium bg-white/60 rounded-xl border border-dashed border-green-200 italic">No completed orders yet</div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col h-full min-h-0 space-y-4">
                            <div className="flex-1 flex flex-col min-h-0 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/70">
                                <h4 className="text-sm font-black text-blue-900 tracking-tight px-1 pb-2 flex-shrink-0" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Active Orders</h4>
                                <div className="flex-grow overflow-y-auto min-h-0 pr-1 select-none">
                                    {activeGroups.length === 0 ? (
                                        <div className="text-center py-6 text-blue-400 text-xs font-medium bg-white/60 rounded-xl border border-dashed border-blue-200 italic">No active orders</div>
                                    ) : activeGroups.map(renderOrderCard)}
                                </div>
                            </div>
                            <div className="flex-1 flex flex-col min-h-0 p-4 bg-green-50/50 rounded-2xl border border-green-100/70">
                                <h4 className="text-sm font-black text-green-900 tracking-tight px-1 pb-2 flex-shrink-0" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Completed Orders</h4>
                                <div className="flex-grow overflow-y-auto min-h-0 pr-1 select-none">
                                    {completedGroups.length === 0 ? (
                                        <div className="text-center py-6 text-green-400 text-xs font-medium bg-white/60 rounded-xl border border-dashed border-green-200 italic">No completed orders yet</div>
                                    ) : completedGroups.map(renderOrderCard)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 flex-shrink-0 text-left">
                    <DownloadAppPrompt
                        title="Get the app 📲"
                        subtitle="For the best experience, download our app to track your orders and more."
                        titleClassName="text-xl font-bold text-green-600 mb-2"
                    />
                </div>
            </div>
        </div>
    );
};

export default TrackOrdersSidebar;